(() => {
  const FLAG = '__TEABALLS_PROMOTION_CENTER_LOADED__';
  if (window[FLAG] && window.TeaballsPromotion?.refresh) {
    window.TeaballsPromotion.refresh();
    return;
  }
  const EVENT_READY = 'teaballs:promotion:ready';
  const EVENT_CHANGE = 'teaballs:promotion:change';
  const EVENT_TICK = 'teaballs:promotion:tick';
  const listeners = new Set();
  let rawConfig = null;
  let resolvedState = null;
  let clockOffsetMs = 0;
  let tickTimer = 0;

  const asNumber = (value, fallback = 0) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
  };

  const parseDate = (value) => {
    if (!value) return null;
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const nowMs = () => Date.now() + clockOffsetMs;

  const sortTiers = (tiers = []) => [...tiers]
    .map((tier) => ({
      ...tier,
      minQuantity: Math.max(1, asNumber(tier.minQuantity, 1)),
      discountPercent: Math.max(0, asNumber(tier.discountPercent, 0)),
    }))
    .sort((a, b) => a.minQuantity - b.minQuantity);

  const sortGoals = (goals = []) => [...goals]
    .map((goal) => ({ ...goal, quantity: Math.max(1, asNumber(goal.quantity, 1)) }))
    .sort((a, b) => a.quantity - b.quantity);

  const collectionIds = (collection) => new Set((collection?.productIds || []).map((id) => String(id)));

  const thresholdMet = (offer, metrics) => {
    if (!offer) return false;
    if (offer.thresholdType === 'quantity') return metrics.eligibleQuantity >= asNumber(offer.thresholdQuantity, 0);
    if (offer.thresholdType === 'spend') return metrics.eligibleSubtotalCents >= asNumber(offer.thresholdSpendCents, 0);
    return true;
  };

  const commercialMechanicRequired = (config, source) => {
    if (source === 'campaign') {
      const campaign = config.campaign || {};
      const hasPrimary = campaign.primaryOfferType && campaign.primaryOfferType !== 'none';
      const hasGift = (config.gifts || []).some((gift) => gift.activeIn === 'campaign' || gift.activeIn === 'both');
      const hasShippingOverride = campaign.shipping?.mode === 'free' || campaign.shipping?.mode === 'threshold';
      return hasPrimary || hasGift || hasShippingOverride;
    }
    const hasEvergreenTier = Boolean(config.evergreen?.enabled && sortTiers(config.evergreen?.tiers).length);
    const hasEvergreenGift = (config.gifts || []).some((gift) => gift.activeIn === 'evergreen' || gift.activeIn === 'both');
    return hasEvergreenTier || hasEvergreenGift;
  };

  const commercialConfirmed = (config, source) => {
    const sourceConfig = source === 'campaign' ? config.campaign : config.evergreen;
    if (!commercialMechanicRequired(config, source)) return true;
    return sourceConfig?.pricingConfirmed === true;
  };

  const scheduleStatus = (campaign, at = nowMs()) => {
    if (!campaign?.enabled) return { configured: false, inWindow: false, phase: 'disabled', startMs: null, endMs: null };
    const startMs = parseDate(campaign.startAt);
    const endMs = parseDate(campaign.endAt);
    if (campaign.startAt && startMs === null) return { configured: true, inWindow: false, phase: 'invalid_start', startMs, endMs };
    if (campaign.endAt && endMs === null) return { configured: true, inWindow: false, phase: 'invalid_end', startMs, endMs };
    if (startMs !== null && at < startMs) return { configured: true, inWindow: false, phase: 'scheduled', startMs, endMs };
    if (endMs !== null && at >= endMs) return { configured: true, inWindow: false, phase: 'ended', startMs, endMs };
    return { configured: true, inWindow: true, phase: 'active', startMs, endMs };
  };

  const campaignOffer = (campaign) => ({
    type: campaign.primaryOfferType,
    flatPercent: Math.max(0, asNumber(campaign.flatPercent, 0)),
    fixedAmountCents: Math.max(0, asNumber(campaign.fixedAmountCents, 0)),
    thresholdType: campaign.thresholdType,
    thresholdQuantity: Math.max(0, asNumber(campaign.thresholdQuantity, 0)),
    thresholdSpendCents: Math.max(0, asNumber(campaign.thresholdSpendCents, 0)),
    tiers: sortTiers(campaign.tiers),
    bxgy: campaign.bxgy || {},
  });

  const evergreenOffer = (evergreen) => ({
    type: 'tiered',
    flatPercent: 0,
    fixedAmountCents: 0,
    thresholdType: 'none',
    thresholdQuantity: 0,
    thresholdSpendCents: 0,
    tiers: sortTiers(evergreen.tiers),
    bxgy: {},
  });

  const effectiveShipping = (config, source) => {
    if (source === 'campaign') {
      const mode = config.campaign.shipping?.mode || 'inherit';
      if (mode === 'free') return { mode: 'free', thresholdCents: 0 };
      if (mode === 'threshold') return { mode: 'threshold', thresholdCents: Math.max(0, asNumber(config.campaign.shipping?.thresholdCents, 0)) };
    }
    const thresholdCents = Math.max(0, asNumber(config.evergreen.shippingThresholdCents, 0));
    return { mode: thresholdCents > 0 ? 'threshold' : 'none', thresholdCents };
  };

  const activeGifts = (config, source) => (config.gifts || []).filter((gift) => {
    if (gift.activeIn === 'both') return true;
    return gift.activeIn === source;
  });

  const diagnosticsFor = (config, state, schedule) => {
    const issues = [];
    if (!config.master?.enabled) issues.push({ level: 'info', code: 'system_off', message: 'Promotion Center is disabled.' });
    if (config.evergreen?.enabled && !config.evergreen?.eligibleCollection?.id) issues.push({ level: 'warning', code: 'evergreen_collection_missing', message: 'Evergreen eligible collection is not configured.' });
    if ((config.evergreen?.eligibleCollection?.productCount || 0) > 50) issues.push({ level: 'warning', code: 'evergreen_collection_truncated', message: 'Evergreen eligible collection exceeds 50 products; storefront eligibility JSON is truncated.' });
    if ((config.campaign?.eligibleCollection?.productCount || 0) > 50) issues.push({ level: 'warning', code: 'campaign_collection_truncated', message: 'Campaign eligible collection exceeds 50 products; storefront eligibility JSON is truncated.' });
    if (config.evergreen?.enabled && sortTiers(config.evergreen?.tiers).length === 0) issues.push({ level: 'warning', code: 'evergreen_tiers_missing', message: 'Evergreen programme has no tier blocks.' });
    if (schedule.phase === 'invalid_start' || schedule.phase === 'invalid_end') issues.push({ level: 'error', code: 'campaign_schedule_invalid', message: 'Campaign start or end time is not valid ISO 8601.' });
    if (schedule.inWindow && config.master?.failClosed && !commercialConfirmed(config, 'campaign')) issues.push({ level: 'error', code: 'campaign_pricing_unconfirmed', message: 'Campaign is in schedule but pricing/reward is not confirmed. Live campaign surfaces are held.' });
    if (state.source === 'evergreen' && config.master?.failClosed && !commercialConfirmed(config, 'evergreen')) issues.push({ level: 'error', code: 'evergreen_pricing_unconfirmed', message: 'Evergreen pricing is not confirmed. Live promotional surfaces are held.' });
    if (config.campaign?.primaryOfferType === 'tiered' && sortTiers(config.campaign?.tiers).length === 0) issues.push({ level: 'error', code: 'campaign_tiers_missing', message: 'Campaign is set to tiered but has no campaign tier blocks.' });
    if (config.campaign?.primaryOfferType === 'bxgy' && !config.campaign?.bxgy?.rewardProductId && !config.campaign?.bxgy?.rewardCollectionId) issues.push({ level: 'error', code: 'bxgy_reward_missing', message: 'BXGY campaign has no reward product or reward collection.' });
    for (const gift of config.gifts || []) {
      if (!gift.productId) issues.push({ level: 'warning', code: `gift_product_missing_${gift.id}`, message: `Gift block ${gift.label || gift.id} has no product.` });
    }
    for (const [fragment, count] of Object.entries(config.__duplicates || {})) {
      issues.push({ level: 'error', code: `duplicate_${fragment}`, message: `Promotion Center has ${count} ${fragment} fragments. Keep exactly one.` });
    }
    return issues;
  };

  const resolveState = (config) => {
    if (!config) return null;
    const schedule = scheduleStatus(config.campaign);
    const editorPreview = config.designMode && config.master?.previewCampaignInEditor && config.campaign?.enabled;
    let source = 'off';
    let status = 'inactive';
    let reason = 'system_disabled';

    if (editorPreview) {
      source = 'campaign';
      status = 'active';
      reason = 'theme_editor_preview';
    } else if (config.master?.enabled) {
      if (schedule.inWindow) {
        source = 'campaign';
        const confirmed = commercialConfirmed(config, 'campaign');
        const held = config.master?.failClosed && !confirmed;
        status = held ? 'held' : 'active';
        reason = held ? 'campaign_pricing_unconfirmed' : 'campaign_active';
      } else if (schedule.phase === 'ended' && config.campaign?.endBehavior === 'off') {
        source = 'off';
        status = 'inactive';
        reason = 'campaign_ended_off';
      } else if (config.evergreen?.enabled) {
        source = 'evergreen';
        const confirmed = commercialConfirmed(config, 'evergreen');
        const held = config.master?.failClosed && !confirmed;
        status = held ? 'held' : 'active';
        reason = held ? 'evergreen_pricing_unconfirmed' : 'evergreen_active';
      } else {
        source = 'off';
        status = 'inactive';
        reason = schedule.phase === 'scheduled' ? 'campaign_not_started' : 'no_evergreen';
      }
    }

    const sourceConfig = source === 'campaign' ? config.campaign : config.evergreen;
    const state = {
      schemaVersion: config.schemaVersion,
      source,
      status,
      reason,
      schedule,
      publicName: source === 'campaign' ? config.campaign?.publicName : config.evergreen?.publicName,
      offer: source === 'campaign' ? campaignOffer(config.campaign) : evergreenOffer(config.evergreen),
      eligibleCollection: sourceConfig?.eligibleCollection || null,
      shipping: effectiveShipping(config, source),
      gifts: source === 'off' ? [] : activeGifts(config, source),
      stacking: config.stacking || {},
      surfaces: source === 'campaign' ? config.campaign?.surfaces || {} : config.evergreen?.surfaces || {
        announcement: false,
        mixDock: true,
        pdp: true,
        cart: true,
        productBadges: false,
        homepageStrip: false,
      },
      copy: source === 'campaign' ? config.campaign?.copy || {} : {},
      countdown: source === 'campaign' ? config.campaign?.countdown || {} : { mode: 'off', label: '' },
      basketGoals: source === 'campaign' ? sortGoals(config.campaign?.basketGoals) : [],
      mix: config.mix || {},
      shop: config.shop || {},
      diagnostics: [],
    };
    state.diagnostics = diagnosticsFor(config, state, schedule);
    return state;
  };

  const parseJsonNode = (node) => {
    try {
      return JSON.parse(node.textContent || '{}');
    } catch (error) {
      console.error('[TEABALLS Promotion Center] Invalid JSON fragment.', node.dataset.teaballsPromotionFragment, error);
      return null;
    }
  };

  const parseConfigFromDom = () => {
    const nodes = [...document.querySelectorAll('script[data-teaballs-promotion-fragment]')];
    if (!nodes.length) return null;
    const fragments = {};
    const duplicates = {};
    for (const node of nodes) {
      const type = node.dataset.teaballsPromotionFragment;
      if (!type) continue;
      if (fragments[type]) duplicates[type] = (duplicates[type] || 1) + 1;
      const parsed = parseJsonNode(node);
      if (parsed && !fragments[type]) fragments[type] = parsed;
    }
    const core = fragments.core;
    if (!core) {
      console.error('[TEABALLS Promotion Center] Core fragment is missing.');
      return null;
    }
    const campaign = fragments.campaign || {};
    if (!campaign.eligibleCollection?.id) campaign.eligibleCollection = core.evergreen?.eligibleCollection || null;
    campaign.surfaces = { announcement: false, mixDock: false, pdp: false, cart: false, productBadges: false, homepageStrip: false };
    campaign.copy = campaign.copy || { headline: '', supporting: '', ctaLabel: '', ctaUrl: '', terms: '', announcement: '', mixDock: '', pdp: '', cart: '', badge: '' };
    for (const [key, value] of Object.entries(campaign)) {
      if (!key.startsWith('surface_') || !value?.surface) continue;
      campaign.surfaces[value.surface] = Boolean(value.enabled);
      if (value.copyOverride) campaign.copy[value.surface === 'productBadges' ? 'badge' : value.surface] = value.copyOverride;
      delete campaign[key];
    }
    return {
      ...core,
      campaign,
      gifts: fragments.gifts?.gifts || [],
      __duplicates: duplicates,
      __fragments: Object.keys(fragments),
    };
  };

  const stateSignature = (state) => JSON.stringify({
    source: state?.source,
    status: state?.status,
    reason: state?.reason,
    publicName: state?.publicName,
    offer: state?.offer,
    shipping: state?.shipping,
    gifts: state?.gifts,
    surfaces: state?.surfaces,
    copy: state?.copy,
    schedule: state?.schedule?.phase,
  });

  const dispatch = (type, detail) => {
    document.dispatchEvent(new CustomEvent(type, { detail }));
    for (const listener of listeners) {
      try { listener(detail); } catch (error) { console.error('[TEABALLS Promotion Center] Subscriber error.', error); }
    }
  };

  const refresh = ({ forceReady = false } = {}) => {
    const previousSignature = stateSignature(resolvedState);
    const parsed = parseConfigFromDom();
    if (!parsed) return null;
    rawConfig = parsed;
    const serverEpochMs = asNumber(parsed.serverEpochMs, Date.now());
    clockOffsetMs = serverEpochMs - Date.now();
    resolvedState = resolveState(rawConfig);
    const nextSignature = stateSignature(resolvedState);
    if (forceReady) dispatch(EVENT_READY, resolvedState);
    if (previousSignature && previousSignature !== nextSignature) dispatch(EVENT_CHANGE, resolvedState);
    scheduleTick();
    return resolvedState;
  };

  const countdown = () => {
    const state = resolvedState;
    if (!state || state.source !== 'campaign' || state.status !== 'active') return { visible: false, remainingMs: 0, text: '' };
    const endMs = state.schedule?.endMs;
    if (!endMs) return { visible: false, remainingMs: 0, text: '' };
    const remainingMs = Math.max(0, endMs - nowMs());
    const mode = state.countdown?.mode || 'off';
    const limits = { last_48h: 48, last_24h: 24, last_12h: 12 };
    let visible = mode === 'always';
    if (mode in limits) visible = remainingMs <= limits[mode] * 60 * 60 * 1000;
    if (mode === 'off' || remainingMs <= 0) visible = false;
    const totalSeconds = Math.floor(remainingMs / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const parts = [];
    if (days) parts.push(`${days}d`);
    parts.push(`${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`);
    return { visible, remainingMs, text: parts.join(' '), label: state.countdown?.label || '' };
  };

  const scheduleTick = () => {
    clearTimeout(tickTimer);
    if (!resolvedState || resolvedState.source !== 'campaign') return;
    const data = countdown();
    const delay = data.visible ? 1000 : 30000;
    tickTimer = window.setTimeout(() => {
      const previousSignature = stateSignature(resolvedState);
      resolvedState = resolveState(rawConfig);
      const nextSignature = stateSignature(resolvedState);
      if (previousSignature !== nextSignature) dispatch(EVENT_CHANGE, resolvedState);
      dispatch(EVENT_TICK, { state: resolvedState, countdown: countdown() });
      scheduleTick();
    }, delay);
  };

  const tierProgress = (quantity, tiers = resolvedState?.offer?.tiers || []) => {
    const sorted = sortTiers(tiers);
    let current = null;
    let next = null;
    for (const tier of sorted) {
      if (quantity >= tier.minQuantity) current = tier;
      else { next = tier; break; }
    }
    return {
      current,
      next,
      remaining: next ? Math.max(0, next.minQuantity - quantity) : 0,
      maxReached: Boolean(sorted.length && current && current.id === sorted[sorted.length - 1].id),
    };
  };

  const goalProgress = (quantity, goals = resolvedState?.basketGoals || []) => {
    const sorted = sortGoals(goals);
    let current = null;
    let next = null;
    for (const goal of sorted) {
      if (quantity >= goal.quantity) current = goal;
      else { next = goal; break; }
    }
    return { current, next, remaining: next ? Math.max(0, next.quantity - quantity) : 0 };
  };

  const metricsFromCart = (cart) => {
    const state = resolvedState;
    const ids = collectionIds(state?.eligibleCollection);
    const items = Array.isArray(cart?.items) ? cart.items : [];
    let eligibleQuantity = 0;
    let eligibleSubtotalCents = 0;
    for (const item of items) {
      if (!ids.has(String(item.product_id))) continue;
      eligibleQuantity += asNumber(item.quantity, 0);
      eligibleSubtotalCents += asNumber(item.original_line_price ?? item.line_price ?? item.final_line_price, 0);
    }
    const cartSubtotalCents = asNumber(cart?.items_subtotal_price ?? cart?.original_total_price ?? cart?.total_price, 0);
    const metrics = { eligibleQuantity, eligibleSubtotalCents, cartSubtotalCents };
    const offer = state?.offer || {};
    let expectedDiscountCents = 0;
    let discountPercent = 0;
    if (offer.type === 'tiered') {
      const progress = tierProgress(eligibleQuantity, offer.tiers);
      discountPercent = asNumber(progress.current?.discountPercent, 0);
      expectedDiscountCents = Math.round(eligibleSubtotalCents * discountPercent / 100);
    } else if (offer.type === 'flat_percent' && thresholdMet(offer, metrics)) {
      discountPercent = asNumber(offer.flatPercent, 0);
      expectedDiscountCents = Math.round(eligibleSubtotalCents * discountPercent / 100);
    } else if (offer.type === 'fixed_amount' && thresholdMet(offer, metrics)) {
      expectedDiscountCents = Math.min(eligibleSubtotalCents, asNumber(offer.fixedAmountCents, 0));
    }
    const gifts = (state?.gifts || []).map((gift) => {
      const qualified = gift.triggerType === 'cart_subtotal'
        ? cartSubtotalCents >= asNumber(gift.thresholdSpendCents, 0)
        : eligibleQuantity >= asNumber(gift.thresholdQuantity, 0);
      const remaining = gift.triggerType === 'cart_subtotal'
        ? Math.max(0, asNumber(gift.thresholdSpendCents, 0) - cartSubtotalCents)
        : Math.max(0, asNumber(gift.thresholdQuantity, 0) - eligibleQuantity);
      return { ...gift, qualified, remaining };
    });
    const shippingRemainingCents = state?.shipping?.mode === 'threshold'
      ? Math.max(0, asNumber(state.shipping.thresholdCents, 0) - cartSubtotalCents)
      : 0;
    return {
      ...metrics,
      discountPercent,
      expectedDiscountCents,
      expectedEligibleTotalCents: Math.max(0, eligibleSubtotalCents - expectedDiscountCents),
      tier: tierProgress(eligibleQuantity),
      goal: goalProgress(eligibleQuantity),
      gifts,
      shippingRemainingCents,
    };
  };

  const formatMoney = (cents, currency = resolvedState?.shop?.currency || 'EUR', locale = resolvedState?.shop?.locale || 'de-DE') => {
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(asNumber(cents, 0) / 100);
    } catch {
      return `${(asNumber(cents, 0) / 100).toFixed(2)} ${currency}`;
    }
  };

  const surfaceEnabled = (name) => Boolean(resolvedState?.status === 'active' && resolvedState?.surfaces?.[name]);

  const subscribe = (listener) => {
    if (typeof listener !== 'function') return () => {};
    listeners.add(listener);
    return () => listeners.delete(listener);
  };

  const api = {
    version: '1.0.0',
    current: () => resolvedState,
    raw: () => rawConfig,
    refresh: () => refresh(),
    now: nowMs,
    countdown,
    tierProgress,
    goalProgress,
    metricsFromCart,
    formatMoney,
    surfaceEnabled,
    subscribe,
  };

  window.TeaballsPromotion = api;

  window[FLAG] = true;

  const init = () => refresh({ forceReady: true });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target?.querySelector?.('script[data-teaballs-promotion-fragment]')) refresh();
  });
  document.addEventListener('shopify:section:unload', () => refresh());
})();
